import React, {useMemo} from 'react';
import {Flex, Select, Text} from '@priority-software/priority-style-react';
import classNames from 'classnames';

import {DatePicker as ReactDatePicker} from '../../../../../../components/commonOldtoNew';
import {getLang} from '../../../../../../Localization';
import {
    getStringFormatByColumnType,
    isDateTime as getIsDateTime
} from '../../../../../../features/Form/utils/date';
import styles from '../../styles.module.scss';
import {Operator} from '../../../../../../_legacy/Searches/consts/operators';
import {ISearchCondition} from '../../../../types/filters';
import {getDateRanges, getDateRangesMap} from '../../../../../../common/dateRange';
import {ISearchField} from '../../../../../../common/searchType';
import {getLocalizedText, texts} from '../../../../../../constants/TextConst';
import {getFormattedDates} from '../../../../../../utils/getFormattedDates';
import {createDataTestId} from '../../../../../../utils/dataTestId';
import {formatDate as formatDateByType} from '../../../../../Form/utils/date';

interface Props {
    filter: ISearchCondition;
    column: ISearchField;
    onUpdate: (value: any) => void;
    isDisabled: boolean;
}

const DateControl = ({filter, column, onUpdate, isDisabled}: Props) => {
    const {operator} = filter;
    const {type} = column;

    const filterValues = useMemo(() => {
        const enumValues = filter?.dateEnum || [];
        return enumValues.length > 0
            ? enumValues.map((value, i) => {
                  const dateRangeMap = getDateRangesMap();
                  return dateRangeMap[value]
                      ? formatDateByType(dateRangeMap[value]?.getDate(), type)
                      : filter.values?.[i];
              })
            : filter?.values || [];
    }, [filter.dateEnum, filter?.values]);

    const handleChangeMin = value => {
        const maxValue = !filterValues?.[1] ? '' : filterValues[1];
        onUpdate([value, maxValue]);
    };

    const handleChangeMax = value => {
        const minValue = !filterValues?.[0] ? '' : filterValues[0];
        onUpdate([minValue, value]);
    };
    // Filter values can be null
    const dates = operator === Operator.Between ? filterValues : [filterValues[0]];

    return (
        <Flex
            {...createDataTestId('date-control-filter-box', true)}
            direction="column"
            componentClasses={classNames('date-picker', styles.pickersWrapper)}
        >
            <Flex alignItems="flex-start" componentClasses={styles.dateBetweenWrapper}>
                {operator === Operator.Between && (
                    <Text componentClasses={styles.datePickerText}>
                        {getLocalizedText(texts.from)}
                    </Text>
                )}
                <DatePicker
                    dateEnum={filter.dateEnum}
                    isDisabled={isDisabled}
                    dates={dates}
                    isStart={true}
                    type={type}
                    onChange={handleChangeMin}
                />
            </Flex>
            {operator === Operator.Between && (
                <Flex alignItems="flex-start" componentClasses={styles.dateBetweenWrapper}>
                    <Text componentClasses={styles.datePickerText}>
                        {getLocalizedText(texts.to)}
                    </Text>
                    <DatePicker
                        dateEnum={filter.dateEnum}
                        isStart={false}
                        dates={dates}
                        type={type}
                        onChange={handleChangeMax}
                    />
                </Flex>
            )}
        </Flex>
    );
};

const DatePicker = ({dateEnum, type, onChange, dates, isStart, isDisabled = false}) => {
    const customRange = {value: 'custom', label: getLocalizedText(texts.customRange)};
    const formattedDates = getFormattedDates(dates, type, dateEnum);
    const valueIndex = isStart ? 0 : 1;
    const date = formattedDates[valueIndex];
    const dateRanges = getDateRangesMap();
    const rangeValue = dateEnum ? dateEnum[valueIndex] : dates[valueIndex];
    const dateRange = date ? dateRanges[rangeValue] || customRange : '';
    const isDateTime = getIsDateTime(type);
    const lang = getLang();
    const format = getStringFormatByColumnType(type, lang);

    const handleChange = (date: Date) => {
        const formattedDate = !date || typeof date === 'string' ? date : date.toISOString();
        onChange(formattedDate);
    };

    const handleChangeDateRange = dateRange => {
        onChange(dateRange.value);
    };

    return (
        <Flex
            componentClasses={styles.pickerWrapper}
            {...createDataTestId('date-picker-container', true)}
        >
            <Select
                size="medium"
                options={getDateRanges()}
                onChange={(value: any, data: any) => {
                    handleChangeDateRange(value);
                }}
                isSearchable={false}
                placeholder={null}
                componentClasses={classNames('date-range', styles.rangeSelect)}
                value={dateRange}
                menuScrollMaxHeight={240}
                isDisabled={isDisabled}
            />
            <div className={styles.datePicker} {...createDataTestId('react-date-picker', false)}>
                <ReactDatePicker
                    className={styles.picker}
                    dateFormat={format}
                    selected={date}
                    selectsStart={isStart}
                    selectsEnd={!isStart}
                    startDate={formattedDates[0]}
                    endDate={formattedDates[1]}
                    onChange={handleChange}
                    calendarClassName={`custom`}
                    showTimeSelect={isDateTime}
                    disabled={isDisabled}
                    timeFormat={format.slice(9)}
                />
            </div>
        </Flex>
    );
};
export default DateControl;
